import {authFetch} from "../../authentication/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

function normalizeTrackerType(type) {
    if (!type) return 'NUMERIC';
    const normalized = String(type).toUpperCase();
    if (normalized === 'BOOLEAN' || normalized === 'NUMERIC') return normalized;
    return normalized;
}

export function normalizeTracker(tracker) {
    if (!tracker) return null;

    const userId = tracker.userId ?? tracker.user_id ?? null;
    const title = tracker.title ?? tracker.name ?? tracker.text ?? '';
    const description = tracker.description ?? tracker.desc ?? '';
    const type = normalizeTrackerType(tracker.type ?? tracker.valueType ?? tracker.value_type ?? tracker.trackerType ?? tracker.tracker_type);
    const targetValue = tracker.targetValue ?? tracker.target_value ?? tracker.numericTarget ?? tracker.numeric_target ?? null;
    const targetUnit = tracker.targetUnit ?? tracker.target_unit ?? tracker.unit ?? '';
    const scheduleType = tracker.scheduleType ?? tracker.schedule_type ?? 'DAILY';
    const score = tracker.score ?? null;

    return {
        ...tracker,
        userId,
        user_id: userId,
        title,
        name: title,
        description,
        type,
        valueType: type,
        value_type: type,
        trackerType: type,
        tracker_type: type,
        targetValue,
        target_value: targetValue,
        numericTarget: targetValue,
        numeric_target: targetValue,
        targetUnit,
        target_unit: targetUnit,
        unit: targetUnit,
        scheduleType,
        schedule_type: scheduleType,
        score,
    };
}

export function normalizeTrackerEntry(entry) {
    if (!entry) return null;

    const userId = entry.userId ?? entry.user_id ?? null;
    const trackerId = entry.trackerId ?? entry.tracker_id ?? null;
    const loggedDate = entry.loggedDate ?? entry.logged_date ?? null;
    const numericValue = entry.numericValue ?? entry.numeric_value ?? null;
    const completed = entry.completed ?? null;
    const note = entry.note ?? '';

    return {
        ...entry,
        userId,
        user_id: userId,
        trackerId,
        tracker_id: trackerId,
        loggedDate,
        logged_date: loggedDate,
        numericValue,
        numeric_value: numericValue,
        completed,
        note,
    };
}

function serializeTracker(trackerData) {
    const title = trackerData.title ?? trackerData.name ?? '';
    const description = trackerData.description ?? '';
    const type = normalizeTrackerType(trackerData.type ?? trackerData.valueType ?? trackerData.value_type ?? trackerData.trackerType);
    const customDays = Array.isArray(trackerData.customDays)
        ? trackerData.customDays
        : Array.isArray(trackerData.custom_days)
            ? trackerData.custom_days
            : [];
    const payload = {
        title,
        description,
        value_type: type,
        schedule_type: trackerData.scheduleType ?? trackerData.schedule_type ?? 'DAILY',
        custom_days: customDays,
    };

    if (Object.prototype.hasOwnProperty.call(trackerData, 'score')) {
        payload.score = trackerData.score === '' || trackerData.score == null ? null : Number(trackerData.score);
    }

    if (type === 'NUMERIC') {
        const rawTargetValue = trackerData.targetValue ?? trackerData.numericTarget ?? trackerData.target_value ?? trackerData.numeric_target;
        payload.target_value = rawTargetValue === '' || rawTargetValue == null ? null : Number(rawTargetValue);
        const targetUnit = trackerData.targetUnit ?? trackerData.target_unit ?? trackerData.unit ?? '';
        payload.target_unit = targetUnit || '';
    } else {
        payload.target_value = null;
        payload.target_unit = '';
    }

    return payload;
}

function serializeTrackerEntry(entryData) {
    const payload = {
        logged_date: entryData.loggedDate ?? entryData.logged_date ?? Date.now(),
    };

    if (entryData.id) {
        payload.id = entryData.id;
    }

    if (entryData.note != null && String(entryData.note).trim() !== '') {
        payload.note = String(entryData.note).trim();
    }

    const numericValue = entryData.numericValue ?? entryData.numeric_value;
    if (numericValue !== undefined) {
        payload.numeric_value = numericValue === '' || numericValue == null ? null : Number(numericValue);
    }

    if (entryData.completed !== undefined) {
        payload.completed = Boolean(entryData.completed);
    }

    return payload;
}

export async function fetchTrackerById(token, trackerId) {
    const response = await authFetch(`${API_URL}/api/tracker/${trackerId}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch tracker');
    return normalizeTracker(await response.json());
}

export async function fetchTrackersOfUser(token) {
    const response = await authFetch(`${API_URL}/api/trackers`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch trackers');
    const payload = await response.json();
    const trackers = Array.isArray(payload) ? payload : payload?.items ?? [];
    return trackers.map(normalizeTracker).filter(Boolean);
}

export async function createTracker(token, trackerData) {
    const response = await authFetch(`${API_URL}/api/tracker`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(serializeTracker(trackerData)),
    });

    if (!response.ok) throw new Error('Failed to create tracker');
    return normalizeTracker(await response.json());
}

export async function updateTracker(token, trackerId, trackerData) {
    const response = await authFetch(`${API_URL}/api/tracker/${trackerId}`, {
        method: 'PUT',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(serializeTracker(trackerData)),
    });

    if (!response.ok) throw new Error('Failed to update tracker');
    return normalizeTracker(await response.json());
}

export async function deleteTracker(token, trackerId) {
    const response = await authFetch(`${API_URL}/api/tracker/${trackerId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to delete tracker');
    return response.json();
}

export async function fetchTrackerEntries(token, trackerId) {
    const response = await authFetch(`${API_URL}/api/tracker/${trackerId}/entries`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) throw new Error('Failed to fetch tracker entries');
    const payload = await response.json();
    const entries = Array.isArray(payload) ? payload : payload?.items ?? [];
    return entries.map(normalizeTrackerEntry).filter(Boolean);
}

export async function fetchTrackerScore(token, date, timezone) {
    const params = new URLSearchParams({ date, timezone });
    const response = await authFetch(`${API_URL}/api/trackers/score?${params}`, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) throw new Error('Failed to fetch tracker score');
    return response.json();
}

export async function createTrackerEntry(token, trackerId, entryData) {
    const response = await authFetch(`${API_URL}/api/tracker/${trackerId}/entries`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(serializeTrackerEntry(entryData)),
    });

    if (!response.ok) throw new Error('Failed to save tracker entry');
    return normalizeTrackerEntry(await response.json());
}
